(ns dashboard.entity.question.editable-answer-field
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.components.answer :as answer]
            [dashboard.html :as html]))

(defn EditableAnswerField [field-key
                           field-name
                           question->field-value
                           on-save
                           {:keys [suggestion status] :as question}
                           auto-save
                           show-cancel]
  (let [original-field-value (some-> (question->field-value (or suggestion question)))
        [initial-field-value set-initial-field-value] (react/useState
                                                       original-field-value)
        [content-editable set-content-editable] (react/useState false)
        [content set-content] (react/useState (html/strip-markup initial-field-value))
        [content-ref set-content-ref] (react/useState nil)
        [field-ref set-field-ref] (react/useState nil)

        set-content-editable (fn [value]
                               ;; On first save, the content is getting duplicated
                               ;; on screen - but this digs into the contenteditable
                               ;; dom element and clears the content, which is refreshed
                               ;; on render - compensating for some sketchy non-reactive
                               ;; element issues...
                               (when (and content-editable
                                          (or (false? value)
                                              (and (fn? value)
                                                   (false? (value content-editable)))))
                                 (-> field-ref
                                     .-children
                                     array-seq
                                     first
                                     .-children
                                     array-seq
                                     first
                                     (aset "innerHTML" "")))
                               (set-content-editable value))

        handle-blur (fn [e]
                      (cond
                        (and auto-save (not show-cancel))
                        (do
                          (on-save question field-key content)
                          (set-initial-field-value content)
                          (set-content-editable false))
                        (and auto-save show-cancel)
                        (when (or (nil? (.-relatedTarget e))
                                  (not (.contains field-ref (.-relatedTarget e))))
                          (on-save question field-key content)
                          (set-initial-field-value content)
                          (set-content-editable false))
                        :else
                        (when (or (nil? (.-relatedTarget e))
                                  (not (.contains field-ref (.-relatedTarget e))))
                          (if (not= initial-field-value content)
                            (.focus content-ref)
                            (set-content-editable false)))))]

    (react/useEffect
     (fn []
       (set-initial-field-value original-field-value)
       (set-content original-field-value)
       (set-content-editable false)
       (fn []))
     #js [original-field-value])

    [:div.is-flex.is-flex-grow-1
     {:ref set-field-ref
      :key (keyword field-name (:ordinal question))
      :class [(when suggestion "ml-2")
              :is-flex-grow-1]}
     (when (and suggestion
                (not (str/blank? initial-field-value)))
       [:button.button.is-small.is-rounded.mr-2
        {:title (str "Copy " field-name " to the answer of this question")
         :on-click #(on-save question field-key (question->field-value suggestion))}
        [:span.icon>i.fas.fa-copy]])

     [:div.is-flex.is-flex-direction-column.is-flex-grow-1.is-align-items-start
      {:class :questions--editable-answer-wrapper}
      [:div.text-layout.is-flex.is-flex-grow-1
       {:title (str "Click to edit " field-name)
        :style {:height "100%"
                :min-height "24px"
                :min-width "100%"}
        :class (cond-> []
                 (not suggestion) (conj "editable-field")
                 ;; To make cursor noticeable, when at start of lines
                 content-editable (conj "p-1"))
        ;; React will warn on using content editable when there are children nodes present,
        ;; to not modify children structure.
        ;; We are not doing that so let's disable the warning
        :suppressContentEditableWarning true
        :content-editable (if content-editable
                            "plaintext-only"
                            false)
        ;; Since the field is contenteditable, on-change will not fire
        ;; and we read the value that the user wrote from the DOM node.
        ;; Using innerText to get text value without markup
        :on-input #(set-content (.. ^js % -target -innerText))
        :on-click (fn [_]
                    (when-not (or suggestion
                                  (.querySelector js/document "[contenteditable=plaintext-only]"))
                      (set-content-editable true)
                      (when content-ref
                        (js/setTimeout (fn [] (.focus content-ref)) 0))))
        :on-blur handle-blur
        :ref set-content-ref}
       (let [tag-class (answer/response-tag-class initial-field-value)]
         (if content-editable
           ;; When contenteditable, we don't control the DOM value, but let user edit the content inside the DOM node directly
           (html/strip-markup initial-field-value)
           (if tag-class
             [:span.tag {:class tag-class} initial-field-value]
             ;; :stop-propagation true is to prevent a link click to set the content editable,
             (html/as-hiccup-with-anchors initial-field-value))))]
      (when (and content-editable (or (not auto-save)
                                      show-cancel))
        [:div.is-flex.is-justify-content-flex-end
         [:button.button.is-danger.is-rounded.is-align-self-flex-end.mt-2
          {:on-blur handle-blur
           :on-click (fn [_]
                       (set-content initial-field-value)
                       (set! (.-innerText content-ref) initial-field-value)
                       (set-content-editable false))}
          "Cancel"]
         (when-not auto-save
          [:button.button.is-primary.is-rounded.is-align-self-flex-end.mt-2.ml-2
          {:disabled (or
                      ;; Allow to empty field, if it initially was not empty
                      (and (str/blank? content)
                           (str/blank? initial-field-value))
                      (= content initial-field-value))
           :on-blur handle-blur
           :on-click (fn [_]
                       (let [commit-edit-event #(on-save
                                                 question
                                                 field-key
                                                ;; On saving, we use the current value that user typed into the DOM node
                                                 (.-innerText content-ref))]
                         (if (= "approved" status)
                           (when (js/confirm "Change answer of approved question?")
                             (commit-edit-event))
                           (commit-edit-event)))
                       (set-initial-field-value (.-innerText content-ref))
                       (set-content-editable false))}
          "Save"])])]]))